import axios from "axios"
import axiosRetry from "axios-retry"
import { RETRY_COUNT, SERVER_API_DEV, SERVER_API_PROD } from "@core/config"
import { limitedRequest } from "./limiter"
import { addIdempotencyKey, preventDuplicateRequest } from "./idempotency"

const BASE_URL = import.meta.env.DEV ? SERVER_API_DEV : SERVER_API_PROD

const remoteInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 15_000,
})

axiosRetry(remoteInstance, {
  retries: RETRY_COUNT,
  retryDelay: retryCount => Math.min(1000 * retryCount, 400),
  retryCondition: error => {
    const method = error.config?.method?.toLowerCase()
    const hasIdempotencyKey = error.config?.headers?.["Idempotency-Key"]

    if (method === "delete") {
      return false
    }

    if (method && ["post", "put", "patch"].includes(method)) {
      return hasIdempotencyKey ? axiosRetry.isNetworkOrIdempotentRequestError(error) : false
    }

    return axiosRetry.isNetworkOrIdempotentRequestError(error)
  },
})

remoteInstance.interceptors.request.use((config: any) => {
  // 기본 baseURL은 고정 (DEV/PROD)
  config.baseURL = BASE_URL

  const method = config.method?.toLowerCase()
  if (method && ["post", "patch", "put"].includes(method)) {
    config.headers = config.headers || {}
    if (!config.headers["Idempotency-Key"]) {
      config.headers["Idempotency-Key"] = addIdempotencyKey({}, method.toUpperCase() as "POST" | "PATCH" | "PUT").headers["Idempotency-Key"]
    }
  }

  return config
})

remoteInstance.interceptors.response.use(
  (response: any) => response,
  (error: any) => {
    const method = error.config?.method?.toLowerCase()
    const hasIdempotencyKey = error.config?.headers?.["Idempotency-Key"]
    const isRetryable =
      (method === "get" || (["post", "put", "patch"].includes(method) && hasIdempotencyKey)) && axiosRetry.isNetworkOrIdempotentRequestError(error)
    const currentRetry = error.config?.["axios-retry"]?.retryCount || 0

    if (!isRetryable || currentRetry >= RETRY_COUNT) {
      console.error(`[RemoteAPI ✕] ${error.code || error.message} → ${error.config?.url}`, error.response?.status)
    }

    return Promise.reject(error)
  }
)

export const remoteServerApi = {
  get: <T = any>(url: string, config?: any): Promise<any> => limitedRequest(() => remoteInstance.get<T>(url, config) as Promise<any>),

  post: <T = any>(url: string, data?: any, config?: any): Promise<any> =>
    preventDuplicateRequest("POST", url, data, () => {
      const configWithIdempotency = addIdempotencyKey(config, "POST")
      return limitedRequest(() => remoteInstance.post<T>(url, data, configWithIdempotency) as Promise<any>)
    }),

  put: <T = any>(url: string, data?: any, config?: any): Promise<any> =>
    preventDuplicateRequest("PUT", url, data, () => {
      const configWithIdempotency = addIdempotencyKey(config, "PUT")
      return limitedRequest(() => remoteInstance.put<T>(url, data, configWithIdempotency) as Promise<any>)
    }),

  patch: <T = any>(url: string, data?: any, config?: any): Promise<any> =>
    preventDuplicateRequest("PATCH", url, data, () => {
      const configWithIdempotency = addIdempotencyKey(config, "PATCH")
      return limitedRequest(() => remoteInstance.patch<T>(url, data, configWithIdempotency) as Promise<any>)
    }),

  delete: <T = any>(url: string, config?: any): Promise<any> => limitedRequest(() => remoteInstance.delete<T>(url, config) as Promise<any>),

  interceptors: remoteInstance.interceptors,
}
